/* Loader Screen — iPad view for the loader cab. Both lanes side by side, live weigh
   section only (no plant graphic / gauges), glove-sized controls, up-next strip. */
import { useEffect, useState, useCallback, useRef } from "react";
import { api, WS_BASE } from "@/lib/api";
import { prepareAutoPrintTab, finishAutoPrint } from "@/lib/autoPrint";
import { usePlant } from "@/context/PlantContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Tablet, Radio, Activity, Truck, LogOut, Volume2, VolumeX, Maximize, MapPin, ArrowLeft, Sun, Moon, Megaphone } from "lucide-react";
import { LANES } from "@/lib/batching";
import { metaFor } from "@/lib/materials";
import { armAudio, audioArmed, autoArmOnFirstGesture, speakTruckBack, speakLoader, getLoaderVoiceOn, setLoaderVoiceOn } from "@/lib/voice";
import { LiveWeights } from "./BatcherCockpit";
import { useScaleFeed } from "./Batcher";
import LoaderGpsDialog from "@/components/LoaderGpsDialog";

function useWakeLock() {
  useEffect(() => {
    let lock = null;
    const grab = async () => { try { lock = await navigator.wakeLock?.request("screen"); } catch { /* not supported */ } };
    grab();
    const onVis = () => { if (document.visibilityState === "visible") grab(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); try { lock?.release(); } catch { /* ignore */ } };
  }, []);
}

function LoaderLoadSize({ batch, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [reuse, setReuse] = useState("");
  const [trim, setTrim] = useState("");
  useEffect(() => { setReuse(batch?.reuse_m3 ? String(batch.reuse_m3) : ""); setTrim(batch?.water_trim_l ? String(batch.water_trim_l) : ""); }, [batch?.id, batch?.reuse_m3, batch?.water_trim_l]);
  if (!batch) return null;
  const locked = Object.values(batch.actual_weights || {}).some((v) => Number(v) > 0);
  const save = async (patch, label) => {
    try { await api.patch(`/batches/${batch.id}`, patch); toast.success(label); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setQty = (q) => { q = Math.round(q * 10) / 10; if (q < 0.5 || q === Number(batch.quantity_m3)) return; save({ quantity_m3: q }, `Load size · ${q} m³`); };
  const commit = () => { setEditing(false); const q = parseFloat(val); if (!isNaN(q)) setQty(q); };
  const stepBtn = "mono text-2xl font-black w-14 h-14 border border-zinc-700 text-white hover:border-primary-orange disabled:opacity-30 disabled:cursor-not-allowed";
  const field = "mono text-lg font-bold w-20 h-12 bg-black border border-zinc-700 focus:border-primary-orange text-white px-2 text-center";
  return (
    <div className="flex items-center gap-4 px-3 py-2 border-b border-zinc-800 bg-zinc-950 shrink-0 flex-wrap" data-testid={`loader-load-size-${batch.lane_id}`}>
      <div>
        <div className="mono text-[9px] uppercase tracking-[0.3em] text-primary-orange">Load size{locked ? " · weighing started" : ""}</div>
        <div className="flex items-center gap-2 mt-1">
          <button type="button" disabled={locked} onClick={() => setQty(Number(batch.quantity_m3) - 0.5)} data-testid={`loader-qty-minus-${batch.lane_id}`} className={stepBtn}>−</button>
          {editing ? (
            <input autoFocus type="number" step="0.1" min="0.5" value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit}
              onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
              data-testid={`loader-qty-input-${batch.lane_id}`} className="mono text-4xl font-black w-32 h-14 bg-black border border-primary-orange text-white px-2 text-center" />
          ) : (
            <button type="button" disabled={locked} onClick={() => { setVal(String(batch.quantity_m3)); setEditing(true); }} data-testid={`loader-qty-${batch.lane_id}`}
              title={locked ? "Weighing has started — clear the load to change size" : "Tap to type a size"}
              style={{ color: "rgb(var(--primary))" }} className="heading text-5xl font-black leading-none tabular-nums flex items-baseline gap-1 px-2 min-w-[7rem] justify-center disabled:opacity-60">
              {Number(batch.quantity_m3).toFixed(1)}<span className="text-lg text-zinc-400">m³</span>
            </button>
          )}
          <button type="button" disabled={locked} onClick={() => setQty(Number(batch.quantity_m3) + 0.5)} data-testid={`loader-qty-plus-${batch.lane_id}`} className={stepBtn}>+</button>
        </div>
      </div>
      <label className="flex flex-col gap-1" title="Reused / returned concrete already in the drum — plant batches less fresh">
        <span className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">Reuse m³</span>
        <input type="number" step="0.1" min="0" value={reuse} onChange={(e) => setReuse(e.target.value)} placeholder="0"
          onBlur={() => { const n = parseFloat(reuse) || 0; if (n !== Number(batch.reuse_m3 || 0)) save({ reuse_m3: n }, `Reuse · ${n} m³`); }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid={`loader-reuse-${batch.lane_id}`} className={field} />
      </label>
      <label className="flex flex-col gap-1" title="Manual water trim for this load (± litres)">
        <span className="mono text-[9px] uppercase tracking-[0.3em] text-sky-400">Water trim L</span>
        <input type="number" step="5" value={trim} onChange={(e) => setTrim(e.target.value)} placeholder="±0"
          onBlur={() => { const n = parseFloat(trim) || 0; if (n !== Number(batch.water_trim_l || 0)) save({ water_trim_l: n }, `Water trim · ${n >= 0 ? "+" : ""}${n} L`); }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid={`loader-trim-${batch.lane_id}`} className={`${field} focus:border-sky-400`} />
      </label>
    </div>
  );
}

export default function LoaderScreen() {
  const { plantId } = usePlant();
  const { user, logout } = useAuth();
  const [batches, setBatches] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [liveStatus, setLiveStatus] = useState("connecting");
  const [soundOn, setSoundOn] = useState(audioArmed());
  const [gpsOpen, setGpsOpen] = useState(false);
  const [voiceOn, setVoiceOn] = useState(getLoaderVoiceOn);
  const [light, setLight] = useState(() => localStorage.getItem("armorbatch.loaderTheme") === "light");
  const toggleTheme = () => { const n = !light; setLight(n); localStorage.setItem("armorbatch.loaderTheme", n ? "light" : "dark"); };
  const scaleFeed = useScaleFeed(plantId);
  useWakeLock();
  useEffect(() => {
    const off = autoArmOnFirstGesture();
    const t = setInterval(() => { if (audioArmed()) { setSoundOn(true); clearInterval(t); } }, 1000);
    return () => { off(); clearInterval(t); };
  }, []);

  const seqRef = useRef(0);
  const load = useCallback(async () => {
    const q = plantId ? `?plant_id=${plantId}` : "";
    const seq = ++seqRef.current;
    const [b, m] = await Promise.all([api.get(`/batches${q}`), api.get(`/mixes`)]);
    if (seq !== seqRef.current) return;
    setBatches(b.data); setMixes(m.data || []);
  }, [plantId]);
  useEffect(() => { load(); const t = setInterval(load, 10_000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    let ws, closed = false, retry = 0, deb = null;
    const connect = () => {
      if (closed) return;
      setLiveStatus("connecting");
      try { ws = new WebSocket(`${WS_BASE}/ws/dispatch`); } catch { setLiveStatus("offline"); return; }
      ws.onopen = () => { retry = 0; setLiveStatus("live"); };
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (plantId && evt.plant_id && evt.plant_id !== plantId) return;
          if ((evt.type || "").startsWith("batch.")) { clearTimeout(deb); deb = setTimeout(load, 300); }
          if (evt.type === "truck.status" && evt.arrived_yard) speakTruckBack(evt.truck_number);
        } catch { /* ignore */ }
      };
      ws.onclose = () => { setLiveStatus("offline"); if (!closed) { retry = Math.min(retry + 1, 5); setTimeout(connect, 1000 * retry); } };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => { closed = true; clearTimeout(deb); try { ws && ws.close(); } catch { /* ignore */ } };
  }, [load, plantId]);

  const active = batches.filter((b) => ["queued", "batching"].includes(b.status));
  const byLane = { "LANE-1": null, "LANE-2": null };
  for (const b of active) if (b.lane_id && (b.lane_id in byLane) && !byLane[b.lane_id]) byLane[b.lane_id] = b;
  const queue = active.filter((b) => !b.lane_id).sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));

  /* ONE LANE AT A TIME. Two lanes side by side on a tablet cut the material rows and
     the second lane off the screen, so the loader only ever sees part of the job.
     Show the lane being loaded full-screen, with a switcher in the header. */
  const [focus, setFocus] = useState(() => localStorage.getItem("armorbatch.loaderLane") || LANES[0]);
  const pickedAt = useRef(0);
  const pickLane = (l, manual = false) => { setFocus(l); if (manual) pickedAt.current = Date.now(); localStorage.setItem("armorbatch.loaderLane", l); };
  useEffect(() => {
    if (byLane[focus]) return;
    if (Date.now() - pickedAt.current < 120_000) return;   // operator chose this lane — leave it
    const busy = LANES.find((l) => byLane[l]);
    if (busy) pickLane(busy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, batches]);

  const fail = (e) => toast.error(e.response?.data?.detail || "Failed");  const assign = async (lane, b) => {
    try {
      await api.post(`/batches/${b.id}/assign-lane`, { lane_id: lane, cement_silo_id: null });
      if (b.status === "queued") await api.post(`/batches/${b.id}/start`);
      toast.success(`${lane} · ${b.truck_number} loaded`); load();
    } catch (e) { fail(e); }
  };
  const doAction = async (id, payload) => { try { await api.post(`/batches/${id}/action`, payload); load(); } catch (e) { fail(e); } };
  const capture = async (id, key, value) => {
    try { await api.post(`/batches/${id}/action`, { action: `capture_${key}`, material: key, actual: value, note: "Scale reading captured" }); toast.success(`${metaFor(key).label} · ${value}`); load(); } catch (e) { fail(e); }
  };
  const setSilo = async (id, lane, silo) => { try { await api.post(`/batches/${id}/assign-lane`, { lane_id: lane, cement_silo_id: silo }); load(); } catch (e) { fail(e); } };
  const completing = useRef(new Set());
  const complete = async (id, auto = false) => {
    if (completing.current.has(id)) return;
    if (!auto && !window.confirm("Complete this load and issue the docket?")) return;
    completing.current.add(id);
    const tab = prepareAutoPrintTab();
    try {
      const res = await api.post(`/batches/${id}/complete`);
      finishAutoPrint(tab, res.data.duplicate || res.data.auto_print === false ? null : res.data.docket);
      toast.success(res.data.duplicate ? "Load already completed" : `Docket ${res.data.docket?.docket_no || ""} issued`);
      load();
    } catch (e) { finishAutoPrint(tab, null); fail(e); }
  };
  const abort = async (id) => {
    const reason = window.prompt("E-STOP reason (optional):", "Loader stopped");
    if (reason === null) return;
    try { await api.post(`/batches/${id}/abort`, { reason }); toast.success("Batch aborted — truck released"); load(); } catch (e) { fail(e); }
  };
  const loadTruck = async (id) => {
    try { await api.post(`/batches/${id}/action`, { action: "load_truck", note: "Discharge started from loader screen" }); load(); } catch (e) { fail(e); }
  };

  const pill = "inline-flex items-center gap-1.5 px-2 py-1 border mono text-[10px] uppercase tracking-widest";
  return (
    <div className={`h-screen w-screen flex flex-col gap-2 p-2 bg-black text-white overflow-hidden select-none ${light ? "loader-light" : ""}`} data-testid="loader-screen" data-theme={light ? "light" : "dark"}>
      <div className="flex items-center gap-2 shrink-0 px-1">
        <div className="heading text-lg font-black uppercase flex items-center gap-2 !text-white"><Tablet className="w-5 h-5 text-primary-orange" /> Loader <span className="text-primary-orange">Screen</span></div>
        <span data-testid="loader-ws-pill" className={`${pill} ${liveStatus === "live" ? "border-primary-orange text-primary-orange" : "border-zinc-700 text-zinc-400"}`}><Radio className="w-3 h-3" />{liveStatus === "live" ? "LIVE" : liveStatus.toUpperCase()}</span>
        <span data-testid="loader-rinstrum-pill" title={scaleFeed.statusText} className={`${pill} ${scaleFeed.state === "streaming" ? "border-emerald-500 text-emerald-400" : scaleFeed.state === "idle" ? "border-amber-500 text-amber-400" : "border-red-700 text-red-400"}`}>
          <Activity className={`w-3 h-3 ${scaleFeed.state === "streaming" ? "animate-pulse" : ""}`} />Rinstrum · {scaleFeed.state === "streaming" ? "LIVE" : scaleFeed.state === "idle" ? "NO WEIGHTS" : "OFFLINE"}
        </span>
        <div className="flex items-stretch border border-zinc-800" data-testid="loader-lane-switcher">
          {LANES.map((l) => {
            const lb = byLane[l];
            return (
              <button key={l} type="button" onClick={() => pickLane(l, true)} data-testid={`loader-lane-tab-${l}`}
                className={`mono text-[11px] uppercase tracking-widest font-black px-4 min-h-[40px] border-r border-zinc-800 last:border-r-0 ${l === focus ? "bg-primary-orange text-black" : "text-zinc-400 hover:text-white"}`}>
                {l.replace("LANE-", "L")} <span className={l === focus ? "text-black/70" : lb ? "text-primary-orange" : "text-zinc-600"}>· {lb ? lb.truck_number : "idle"}</span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          {user?.role !== "loader" && (
            <button type="button" data-testid="loader-back-btn" onClick={() => { window.location.href = "/batcher"; }} className={`${pill} min-h-[40px] border-zinc-700 text-zinc-300 hover:text-white hover:border-primary-orange`}><ArrowLeft className="w-4 h-4" /> Back to cockpit</button>
          )}
          <button type="button" data-testid="loader-voice-btn" title="Spoken countdown: five hundred to go… two fifty… cut"
            onClick={() => { const on = !voiceOn; setLoaderVoiceOn(on); setVoiceOn(on); if (on) { armAudio(true); setSoundOn(true); speakLoader("Call-outs on"); } else { window.speechSynthesis?.cancel?.(); } }}
            className={`${pill} min-h-[40px] ${voiceOn ? "!text-emerald-400 !border-emerald-700" : "border-zinc-700 text-zinc-500"}`}>
            <Megaphone className="w-4 h-4" /> {voiceOn ? "Voice" : "Voice off"}</button>
          <button type="button" data-testid="loader-sound-btn" onClick={() => { armAudio(true); setSoundOn(true); }} className={`${pill} min-h-[40px] ${soundOn ? "!text-emerald-400 !border-emerald-700" : "!text-amber-300 !border-amber-500 animate-pulse"}`}>
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} {soundOn ? "Sound on" : "Tap for sound"}</button>
          <button type="button" data-testid="loader-theme-btn" onClick={toggleTheme} title="Switch light / dark" className={`${pill} min-h-[40px] border-zinc-700 text-zinc-300`}>{light ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />} {light ? "Dark" : "Light"}</button>
          <button type="button" data-testid="loader-gps-btn" onClick={() => setGpsOpen(true)} className={`${pill} min-h-[40px] !border-sky-500 !text-sky-300`}><MapPin className="w-4 h-4" /> GPS · Trucks back</button>
          <button type="button" data-testid="loader-fullscreen-btn" onClick={() => { try { document.fullscreenElement ? document.exitFullscreen?.() : document.documentElement.requestFullscreen?.(); } catch { /* ignore */ } }} className={`${pill} min-h-[40px] border-zinc-700 text-zinc-300`}><Maximize className="w-4 h-4" /> Full screen</button>
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-500 hidden md:inline">{user?.name}</span>
          <button type="button" data-testid="loader-logout-btn" onClick={logout} className={`${pill} min-h-[40px] border-zinc-700 text-zinc-400 hover:text-white`}><LogOut className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 gap-2" data-testid="loader-lanes">
        {LANES.filter((l) => l === focus).map((lane) => {
          const b = byLane[lane];
          const mix = b ? mixes.find((m) => m.id === b.mix_id) : null;
          return (
            <div key={lane} className="flex flex-col min-h-0 min-w-0 border border-zinc-800" data-testid={`loader-lane-${lane}`}>
              <LoaderLoadSize batch={b} onSaved={load} />
              <LiveWeights
                loader lane={lane} batch={b} mix={mix} byLane={byLane} onSelectLane={() => {}} scaleFeed={scaleFeed}
                nextTruck={queue[0]} onNextTruck={() => queue[0] && assign(lane, queue[0])}
                onCapture={(k, v) => b && capture(b.id, k, v)}
                onSiloChange={(s) => b && setSilo(b.id, lane, s)}
                onLoadTruck={() => b && loadTruck(b.id)}
                onComplete={(auto) => b && complete(b.id, auto === true)}
                onAbort={() => b && abort(b.id)}
                onAction={(p) => b && doAction(b.id, p)}
                onRefresh={load}
                onGps={() => setGpsOpen(true)}
              />
            </div>
          );
        })}
      </div>

      <div className="shrink-0 bg-zinc-950 border border-zinc-800 flex items-stretch min-h-[64px]" data-testid="loader-up-next">
        <div className="px-3 flex items-center gap-2 border-r border-zinc-800 mono text-[10px] uppercase tracking-[0.3em] text-primary-orange font-black"><Truck className="w-4 h-4" /> Up next <span className="text-white text-sm" data-testid="loader-queue-count">{queue.length}</span></div>
        <div className="flex-1 flex items-center gap-2 px-2 overflow-x-auto">
          {queue.length === 0 && <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">No trucks waiting</span>}
          {queue.slice(0, 3).map((q, i) => {
            const mix = mixes.find((m) => m.id === q.mix_id);
            return (
              <div key={q.id} className="flex items-center gap-3 border border-zinc-800 bg-black px-3 py-2 min-w-[260px]" data-testid={`loader-next-${i + 1}`}>
                <span className="mono text-[9px] text-zinc-500">#{i + 1}</span>
                <span className="heading text-xl font-black !text-white">{q.truck_number}</span>
                <span className="mono text-[10px] uppercase tracking-widest text-zinc-400 truncate">{mix?.code || q.mix_code} · <span className="text-white font-black">{q.quantity_m3} m³</span></span>
                <span className="mono text-[10px] text-zinc-500 truncate hidden lg:inline">{q.customer_name}</span>
                <div className="ml-auto flex gap-1">
                  {LANES.filter((l) => !byLane[l]).map((l) => (
                    <button key={l} type="button" onClick={() => assign(l, q)} data-testid={`loader-send-${l}-${i + 1}`} className="mono text-[10px] uppercase tracking-widest font-black px-3 py-2 border border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black">→ {l.replace("LANE-", "L")}</button>
                  ))}
                </div>
              </div>
            );
          })}
          {queue.length > 3 && <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">+{queue.length - 3} more</span>}
        </div>
      </div>
      <LoaderGpsDialog open={gpsOpen} onOpenChange={setGpsOpen} />
    </div>
  );
}
